import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Send, Check, Flame } from "lucide-react";
import { toast } from "sonner";

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR",
];

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    state: "",
    gender: "",
    age: "",
    goals: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.goals) {
      return toast.error("Name, email and goals are required");
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
        phone: form.phone || null,
        state: form.state || null,
        gender: form.gender || null,
      };
      await api.post("/leads", payload);
      setSent(true);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (Array.isArray(msg)) toast.error(msg[0]?.msg || "Invalid input");
      else toast.error(msg || "Could not submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian-900 flex flex-col">
      <header className="border-b border-obsidian-600 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/icon-192.png" alt="Ripple Effect" className="h-6 w-6 rounded-sm" />
          <span className="font-heading text-xl">RIPPLE<span className="text-blaze"> EFFECT</span></span>
        </Link>
        <Link to="/auth/login" data-testid="contact-to-login" className="text-xs text-zinc-400 hover:text-blaze uppercase tracking-widest">
          Already a member?
        </Link>
      </header>

      <main className="flex-1 grid place-items-center px-5 py-10">
        <div className="w-full max-w-xl obs-card" data-testid="contact-card">
          {sent ? (
            <div>
              <div className="overline text-blaze mb-2">/ Message received</div>
              <h1 className="font-heading text-4xl uppercase mb-3">We'll be in touch.</h1>
              <p className="text-zinc-400 text-sm mb-6">
                A Ripple Effect coach will reach out to you at{" "}
                <span className="text-zinc-100">{form.email}</span> within 24 hours.
              </p>
              <div className="flex gap-2">
                <Link to="/" className="ghost-btn px-4 py-2 text-xs uppercase tracking-widest">Back home</Link>
                <button
                  type="button"
                  data-testid="contact-send-another"
                  className="blaze-btn px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2"
                  onClick={() => { setSent(false); setForm({ full_name: "", email: "", phone: "", state: "", gender: "", age: "", goals: "" }); }}
                >
                  <Send className="h-3.5 w-3.5" /> Send another
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="overline text-blaze mb-2">/ Coaching inquiry</div>
              <h1 className="font-heading text-4xl uppercase mb-3">Let's chat.</h1>
              <p className="text-zinc-400 text-sm mb-6">
                Tell us about you and your goals. A Ripple Effect coach will follow up personally.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <div className="overline mb-1">Full name *</div>
                  <input
                    data-testid="contact-full-name"
                    type="text"
                    required
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                  />
                </label>
                <label className="block">
                  <div className="overline mb-1">Email *</div>
                  <input
                    data-testid="contact-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                  />
                </label>
                <label className="block">
                  <div className="overline mb-1">Phone</div>
                  <input
                    data-testid="contact-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                  />
                </label>
                <label className="block">
                  <div className="overline mb-1">State</div>
                  <select
                    data-testid="contact-state"
                    value={form.state}
                    onChange={(e) => update("state", e.target.value)}
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                  >
                    <option value="">Select…</option>
                    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="block">
                  <div className="overline mb-1">Gender</div>
                  <select
                    data-testid="contact-gender"
                    value={form.gender}
                    onChange={(e) => update("gender", e.target.value)}
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                  >
                    <option value="">Select…</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="nonbinary">Non-binary</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                </label>
                <label className="block">
                  <div className="overline mb-1">Age</div>
                  <input
                    data-testid="contact-age"
                    type="number"
                    min={13}
                    max={120}
                    value={form.age}
                    onChange={(e) => update("age", e.target.value)}
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                  />
                </label>
              </div>

              <label className="block mt-4">
                <div className="overline mb-1">Your goals *</div>
                <textarea
                  data-testid="contact-goals"
                  required
                  rows={4}
                  value={form.goals}
                  onChange={(e) => update("goals", e.target.value)}
                  placeholder="e.g. I want to lose 15 lbs and build strength before my wedding in October."
                  className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                />
              </label>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="blaze-btn w-full py-3 text-sm mt-6 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                <Flame className="h-4 w-4" />
                {loading ? "Sending…" : "Send inquiry"}
              </button>

              <p className="text-[11px] text-zinc-500 mt-3 uppercase tracking-widest flex items-center gap-2">
                <Check className="h-3 w-3" /> No spam · your info goes straight to a coach
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
